
import { Component, Vue } from "vue-property-decorator";
import GoogleMap from "@/components/GoogleMap.vue";
import AddressDisplay from "@/components/AddressDisplay.vue";
import DisplayImagesCarousel from "@/components/DisplayImagesCarousel.vue";
import { Contact, Facility } from "@/survey";
import PdfGenerator from "@/export/pdf";
import { getContactById, surveyClient } from "@/store";
import ContactDisplayNameEmailCall from "@/components/ContactDisplayNameEmailCall.vue";
import { generateSpreadsheetSingleTableAndDownload } from "@/export/excel";
import { getFacilityFieldValuesForExport, getFacilityHeaderFields } from "@/export/util";

@Component({
  components: {
    GoogleMap,
    AddressDisplay,
    DisplayImagesCarousel,
    ContactDisplayNameEmailCall
  },
  watch: {
    filter() {
      this.$data.currentPage = 1;
    },
    columnFilters: {
      deep: true,
      handler() {
        this.$data.currentPage = 1;
      }
    },
    perPage() {
      this.$data.currentPage = 1;
    }
  }
})
export default class FacilityList extends Vue {
  apiLocation = Vue.prototype.$apiLocation;

  get isPunch(){
    const user = this.$store.state.authentication.currentUser;
    return user !== null && (user.punch || user.admin);
  }

  get canExport() {
    const user = this.$store.state.authentication.currentUser;
    return user !== null && (user.admin || user.export);
  }
  facilityPunchReport(facility: Facility) {
    const tid = `T_${facility.facilityCode}_${new Date().getTime()}`;
    this.$bvToast.toast(`Creating PDF QAQC report for ${facility.name}`, {
      id: tid,
      title: `${facility.facilityCode} QAQC Report`,
      variant: "info",
      noAutoHide: true,
      noCloseButton: true,
      appendToast: true
    });
    surveyClient.getEquipment({ facility: facility.id }).then(equipment => {
      surveyClient.getPunchItems({ facility: facility.id! }).then(punchItems => {
        surveyClient.getPunchFacility({ facility: facility.id! }).then(punchFacility => {
          new PdfGenerator().generatePunchReport(facility, equipment, punchItems, punchFacility).then(
              (pdfDoc: any) => {
                pdfDoc.download(`${facility.name}-qaqc.pdf`);
              })
              .finally(() => {
                this.$bvToast.hide(tid);
              });
        });
      });
    });
  }

  facilityReport(facility: Facility) {
    if (
        confirm(
            "Generating a report can take some time. Please click OK to continue and you will be notified when PDF is ready."
        )
    ) {
      const tid = `T_${facility.facilityCode}_${new Date().getTime()}`;
      this.$bvToast.toast(`Creating PDF report for ${facility.name}`, {
        id: tid,
        title: `${facility.facilityCode} Report`,
        variant: "info",
        noAutoHide: true,
        noCloseButton: true,
        appendToast: true
      });

      surveyClient.getEquipment({ facility: facility.id }).then(equipment => {
        surveyClient.getFencing({ facility: facility.id }).then(fencing => {
          surveyClient.getGates({ facility: facility.id }).then(gates => {
            new PdfGenerator()
                .generateFacilityReport(
                    facility,
                    equipment,
                    fencing,
                    gates,
                    this.$store.state.contacts,
                    this.$store.state.equipmentTypes
                )
                .then((pdfDoc: any) => {
                  pdfDoc.download(`${facility.name}.pdf`);
                })
                .finally(() => {
                  this.$bvToast.hide(tid);
                });
          });
        });
      });
    }
  }

  data() {
    return {
      filter: "",
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      currentPage: 1,
      sortBy: "name",
      sortDesc: false,
      columnFilters: {
        name: "",
        aka: "",
        facilityCode: "",
        surveyTeam: "",
        surveyor: "",
        contact: "",
        status: "",
        qa: ""
      },
      booleanOptions: [
        { value: "", text: "All" },
        { value: "true", text: "Yes" },
        { value: "false", text: "No" }
      ]
    };
  }

  get fields() {
    return [
      { key: "name", sortable: true },
      { key: "aka", sortable: true, label: "AKA" },
      { key: "facilityCode", sortable: true, label: "Code" },
      { key: "surveyTeam", sortable: true, label: "Survey team" },
      { key: "surveyor", sortable: true, label: "Surveyor" },
      { key: "description", sortable: false },
      {
        key: "fullContact",
        label: "Contact",
        sortable: true,
        filterByFormatted: true,
        formatter: (value: Contact | null) => {
          return value?.firstName === undefined || value?.firstName === null
              ? `Unknown`
              : `${value?.firstName} ${value?.lastName}`;
        }
        // thClass: 'd-none', tdClass: 'd-none'
      },
      { key: "status", sortable: true },
      {
        key: "qa", sortable: true, label: "QAQC",
        formatter: (value: boolean | null) => {
          return value === true ? "Yes" : "No";
        },
      },
      {
        key: "qaCompleted", sortable: true, label: "QA Completed",
        formatter: (value: boolean | null) => {
          return value === true ? "Yes" : "No";
        },
      }
      // { key: "priority", sortable: true },
      // { key: "size", sortable: true }
    ];
  }

  get rowCount() {
    return this.filteredFacilities.length;
  }

  get facilities() {
    return this.$store.state.facilities.filter((f: Facility) => {
      return f.survey || this.isPunch; // Remove if user is not QAQC user and facility is not survey facility
    }).map((f: Facility) => {
      return { ...f, fullContact: f.contact ? this.getContact(f.contact) : {} };
    });
  }

  get filteredFacilities() {
    const globalFilter = this.normalize(this.$data.filter);
    const columnFilters = this.$data.columnFilters;
    return this.facilities.filter((facility: any) => {
      if (globalFilter && !this.searchableText(facility).includes(globalFilter)) {
        return false;
      }
      if (!this.matchesTextFilter(facility.name, columnFilters.name)) {
        return false;
      }
      if (!this.matchesTextFilter(facility.aka, columnFilters.aka)) {
        return false;
      }
      if (!this.matchesTextFilter(facility.facilityCode, columnFilters.facilityCode)) {
        return false;
      }
      if (!this.matchesTextFilter(facility.surveyTeam, columnFilters.surveyTeam)) {
        return false;
      }
      if (!this.matchesTextFilter(facility.surveyor, columnFilters.surveyor)) {
        return false;
      }
      if (!this.matchesTextFilter(this.contactName(facility.fullContact), columnFilters.contact)) {
        return false;
      }
      if (columnFilters.status && facility.status !== columnFilters.status) {
        return false;
      }
      if (columnFilters.qa && String(facility.qa === true) !== columnFilters.qa) {
        return false;
      }
      return true;
    });
  }

  get sortedFilteredFacilities() {
    const items = [...this.filteredFacilities];
    const sortBy = this.$data.sortBy;
    const sortDesc = this.$data.sortDesc;
    if (!sortBy) {
      return items;
    }
    return items.sort((a: any, b: any) => {
      const first = this.valueForSort(a, sortBy);
      const second = this.valueForSort(b, sortBy);
      const result = first.localeCompare(second, undefined, { numeric: true, sensitivity: "base" });
      return sortDesc ? result * -1 : result;
    });
  }

  get statusOptions() {
    const statuses = Array.from(new Set(this.facilities.map((facility: Facility) => facility.status).filter(Boolean)));
    return [{ value: "", text: "All" }].concat(
        statuses.sort().map((status: any) => ({ value: status, text: status }))
    );
  }

  get sortLabel() {
    const field = this.fields.find((item: any) => item.key === this.$data.sortBy);
    const direction = this.$data.sortDesc ? "descending" : "ascending";
    return `${field ? field.label || field.key : "Name"} (${direction})`;
  }

  getContact(value: number): Contact | undefined {
    return getContactById(value);
  }

  clearFilters() {
    this.$data.filter = "";
    Object.keys(this.$data.columnFilters).forEach(key => {
      this.$data.columnFilters[key] = "";
    });
    this.$data.currentPage = 1;
  }

  exportPdf() {
    new PdfGenerator()
        .getFacilityTable(this.sortedFilteredFacilities, this.$store.state.contacts, this.$store.state.users)
        .then((pdfDoc: any) => {
          pdfDoc.download("facility-list.pdf");
        });
  }

  exportExcel() {
    const data = [getFacilityHeaderFields()].concat(
        getFacilityFieldValuesForExport(this.sortedFilteredFacilities, this.$store.state.contacts, false)
    );
    generateSpreadsheetSingleTableAndDownload(data, "facility-list.xlsx");
  }

  matchesTextFilter(value: any, filterValue: string) {
    const filter = this.normalize(filterValue);
    if (!filter) {
      return true;
    }
    return this.normalize(value).includes(filter);
  }

  normalize(value: any) {
    return `${value === undefined || value === null ? "" : value}`.toLowerCase();
  }

  searchableText(facility: any) {
    return this.normalize([
      facility.name,
      facility.aka,
      facility.facilityCode,
      facility.surveyTeam,
      facility.surveyor,
      facility.description,
      this.contactName(facility.fullContact),
      facility.status,
      facility.priority,
      facility.size,
      facility.address1,
      facility.address2,
      facility.city,
      facility.state,
      facility.zip,
      facility.qa === true ? "yes" : "no",
      facility.qaCompleted === true ? "yes" : "no"
    ].join(" "));
  }

  contactName(contact: Contact | undefined) {
    return contact?.firstName === undefined || contact?.firstName === null
        ? "Unknown"
        : `${contact.firstName} ${contact.lastName}`;
  }

  valueForSort(item: any, key: string) {
    if (key === "fullContact") {
      return this.contactName(item.fullContact);
    }
    if (key === "qa" || key === "qaCompleted") {
      return item[key] === true ? "Yes" : "No";
    }
    return `${item[key] === undefined || item[key] === null ? "" : item[key]}`;
  }

  sortCompare(firstItem: any, secondItem: any, key: string) {
    return this.valueForSort(firstItem, key).localeCompare(
        this.valueForSort(secondItem, key),
        undefined,
        { numeric: true, sensitivity: "base" }
    );
  }
}
